<script setup lang="ts" generic="A extends GeneratedAccessor">
import { computed, triggerRef, shallowRef, watch } from "vue";
import type {
  GeneratedAccessor,
  GeneratedMeta,
  FieldCustomization,
  FieldLayoutGroup,
  GeneratedProperty,
} from "@/composables/generated/contracts";
import {
  readField,
  writeField,
  resolveFields,
} from "@/composables/generated/fields";
import GeneratedFieldControl from "./GeneratedFieldControl.vue";

const props = defineProps<{
  accessor: A;
  meta: GeneratedMeta;
  fields?: FieldCustomization<A["fields"]>;
  disabled?: boolean;
  formErrors?: string[];
  embedded?: boolean;
  layout?: FieldLayoutGroup[];
}>();
const emit = defineEmits<{ submit: [] }>();
defineSlots<{
  field(props: {
    accessor: A;
    field: {
      key: string;
      label: string;
      readonly: boolean;
      meta: GeneratedProperty;
    };
    value: unknown;
    update: (value: unknown) => void;
    updateField: (key: string, value: unknown) => void;
    error?: string;
    disabled: boolean;
  }): unknown;
  "after-form"(props: { accessor: A }): unknown;
}>();
const draft = shallowRef(props.accessor);
watch(
  () => props.accessor,
  (value) => {
    draft.value = value;
  },
);
const controls = computed(() => {
  const fields = draft.value.fields;
  const overrides =
    typeof props.fields === "function" ? props.fields(fields) : props.fields;
  return resolveFields(props.meta, overrides, fields);
});
const sections = computed(() => {
  const byKey = new Map(controls.value.map((field) => [field.key, field]));
  const grouped = new Set<string>();
  const groups = (props.layout ?? []).flatMap((group) => {
    const fields = group.fields.flatMap((key) => {
      const field = byKey.get(key);
      if (!field || grouped.has(key)) return [];
      grouped.add(key);
      return [field];
    });
    return fields.length
      ? [{ key: group.key, title: group.title, fields, grouped: true }]
      : [];
  });
  const rest = controls.value.filter((field) => !grouped.has(field.key));
  return rest.length
    ? [...groups, { key: "", title: undefined, fields: rest, grouped: false }]
    : groups;
});
function update(key: string, value: unknown) {
  const control = controls.value.find((field) => field.key === key);
  if (props.disabled || !control || control.readonly) return;
  writeField(props.accessor.fields, key, value);
  triggerRef(draft);
}
</script>

<template>
  <component :is="embedded ? 'div' : 'form'" @submit.prevent="emit('submit')">
    <v-alert
      v-if="Object.keys(draft.fieldErrors).length || formErrors?.length"
      type="error"
      role="alert"
      class="mb-4"
    >
      <div v-for="(message, index) in formErrors" :key="`form-${index}`">
        {{ message }}
      </div>
      <div v-for="(message, key) in draft.fieldErrors" :key="key">
        {{ key }}: {{ message }}
      </div>
    </v-alert>
    <div
      v-for="section in sections"
      :key="section.key"
      :class="{ 'generated-form-group': section.grouped }"
      :style="
        section.grouped
          ? {
              '--field-count': section.fields.length,
              '--field-gaps': `${(section.fields.length - 1) * 16}px`,
            }
          : undefined
      "
      :data-group="section.key || undefined"
    >
      <div v-if="section.title" class="generated-form-group-title">
        {{ section.title }}
      </div>
      <template v-for="field in section.fields" :key="field.key">
        <slot
          name="field"
          :accessor="draft"
          :field="field"
          :value="readField(draft.fields, field.key)"
          :update="(value: unknown) => update(field.key, value)"
          :update-field="update"
          :error="draft.fieldErrors[field.key]"
          :disabled="!!disabled || field.readonly"
        >
          <GeneratedFieldControl
            :meta="field.meta"
            :label="field.label"
            :model-value="readField(draft.fields, field.key)"
            :readonly="field.readonly"
            :disabled="disabled"
            :error="draft.fieldErrors[field.key]"
            @update:model-value="update(field.key, $event)"
          />
        </slot>
      </template>
    </div>
    <slot name="after-form" :accessor="draft" />
    <v-btn
      v-if="!embedded"
      type="submit"
      color="primary"
      :loading="disabled"
      :disabled="disabled"
      >Save</v-btn
    >
  </component>
</template>

<style scoped>
.generated-form-group {
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(
      min(
        100%,
        max(180px, calc((100% - var(--field-gaps)) / var(--field-count)))
      ),
      1fr
    )
  );
  column-gap: 16px;
}
.generated-form-group-title {
  grid-column: 1 / -1;
  font-weight: 500;
  margin-bottom: 8px;
}
</style>
